import {useGetCoachInfo} from '@/api/Coach/useGetCoachInfo';
import {useUpdateTeam} from '@/api/CoachAndDirector/useUpdateTeam';
import {useProfiles} from '@/api/User/useProfiles';
import {roleType} from '@/constants/constants';
import {yupResolver} from '@hookform/resolvers/yup';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';
import {EditTeamView} from './EditTeam.view';

type FormTypes = {
  teamName: string;
};

const validationSchema = yup.object().shape({
  teamName: yup.string().required('Team name is required'),
});

export const CoachEditTeam = () => {
  const {teamId} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [editTeamName, setEditTeamName] = useState<boolean>(false);
  const [showTeamColorSelectionModal, setShowTeamColorSelectionModal] = useState<boolean>(false);
  const {currentProfile} = useProfiles();
  const {data: coachInfo, isLoading} = useGetCoachInfo(currentProfile?._id);
  const team = useMemo(() => coachInfo?.teams.find((team) => team._id === teamId), [teamId, coachInfo?.teams]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: {errors},
  } = useForm<FormTypes>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onTeamUpdateSuccessHandler = () => {
    queryClient.invalidateQueries({queryKey: ['coach-info', coachInfo?.coachDetails._id]});
    toast.success('Team updated successfully.');
    setEditTeamName(false);
  };

  const onTeamUpdateErrorHandler = () => {
    toast.error('Failed to update team.');
  };

  const {updateTeam} = useUpdateTeam(onTeamUpdateSuccessHandler, onTeamUpdateErrorHandler, roleType.teamCoach);

  useEffect(() => {
    if (team?.teamColor) {
      setSelectedColor(team.teamColor);
    }
    if (team?.teamName) {
      setValue('teamName', team?.teamName);
    }
  }, [team, setValue]);

  const onClickCancelHandler = () => {
    setEditTeamName(false);
    navigate(`/coach/teams/${teamId}`);
  };

  const onSubmitHandler = async (data: FormTypes) => {
    if (teamId) {
      await updateTeam({
        teamDetails: {...data, teamColor: selectedColor, _id: teamId},
        teamId,
      });
      navigate(`/coach/teams/${teamId}`);
    }
  };

  if (!team) {
    return <div>Team not found</div>;
  }

  return (
    <EditTeamView
      isLoading={isLoading}
      team={team}
      selectedColor={selectedColor}
      setSelectedColor={setSelectedColor}
      showTeamColorSelectionModal={showTeamColorSelectionModal}
      setShowTeamColorSelectionModal={setShowTeamColorSelectionModal}
      editTeamName={editTeamName}
      setEditTeamName={setEditTeamName}
      handleSubmit={handleSubmit}
      onSubmitHandler={onSubmitHandler}
      onClickCancelHandler={onClickCancelHandler}
      register={register}
      errors={errors}
    />
  );
};
